import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { FaEye, FaEyeSlash, FaEnvelope, FaLock, FaKey, FaSignInAlt } from 'react-icons/fa';
import logo from '../assets/images/Logo.png';
import '../App.css';
import { useAuth } from '../context/AuthContext';
import client from '../api/client';
import { toast } from 'react-toastify';

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login: saveSession, logout } = useAuth();
  const registeredEmail = location.state?.registeredEmail || '';
  const registeredName = location.state?.registeredName || '';
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const showLogo = true;
  const getInitialUserType = () => 'admin';

  // Clear any existing session once when arriving on the login page
  const [hasClearedSession, setHasClearedSession] = useState(false);
  useEffect(() => {
    if (!hasClearedSession) {
      logout();
      setHasClearedSession(true);
    }
  }, [hasClearedSession, logout]);

  // Form state
  const [formData, setFormData] = useState({
    userType: getInitialUserType(),
    email: registeredEmail,
    password: '',
    otp: '',
    rememberMe: false
  });

  const [showPassword, setShowPassword] = useState(false);
  const [isOtpMode, setIsOtpMode] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isValidatingOtp, setIsValidatingOtp] = useState(false);

  // Email/Mobile validation
  const validateEmailOrMobile = (value) => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const mobilePattern = /^[0-9]{10,15}$/;
    return emailPattern.test(value) || mobilePattern.test(value);
  };

  // Send OTP via API
  const handleSendOtp = async (e) => {
    e.preventDefault();

    if (!formData.email || !validateEmailOrMobile(formData.email)) {
      toast.error('Please enter a valid email ID');
      return;
    }

    setIsSendingOtp(true);
    try {
      const response = await client.get(`/api/otp/set-otp?emailID=${encodeURIComponent(formData.email)}`);
      const { success, message, data } = response.data || {};

      if (!success) {
        toast.error(message || 'Failed to send OTP');
        setIsSendingOtp(false);
        return;
      }

      if (data?.userID === 0) {
        toast.error(data?.message || message || 'OTP could not be processed');
        setIsSendingOtp(false);
        return;
      }

      if (data?.userID > 0) {
        toast.success('OTP sent successfully to your email');
        setOtpSent(true);
      } else {
        toast.error(message || 'Failed to send OTP');
      }
    } catch (err) {
      const apiMessage = err?.response?.data?.message;
      toast.error(apiMessage || 'Failed to send OTP. Please try again.');
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleResendOtp = async (e) => {
    e.preventDefault();

    if (!formData.email || !validateEmailOrMobile(formData.email)) {
      toast.error('Please enter a valid email ID');
      return;
    }

    setIsSendingOtp(true);
    try {
      const response = await client.get(
        `/api/otp/resend-otp?emailID=${encodeURIComponent(formData.email)}`,
        { headers: { accept: 'text/plain' } }
      );

      // Your backend may return plain text OR JSON.
      // If it's plain text, axios puts it in response.data as string.
      if (typeof response.data === 'string') {
        toast.success(response.data || 'OTP resent successfully');
      } else {
        const { success, message } = response.data || {};
        if (!success) {
          toast.error(message || 'Failed to resend OTP');
          return;
        }
        toast.success(message || 'OTP resent successfully');
      }

      setOtpSent(true);
    } catch (err) {
      const apiMessage = err?.response?.data?.message;
      toast.error(apiMessage || 'Failed to resend OTP. Please try again.');
    } finally {
      setIsSendingOtp(false);
    }
  };


  // Toggle between Password and OTP Login
  const toggleOtpMode = (e) => {
    e.preventDefault();

    if (!isOtpMode) {
      setIsOtpMode(true);
      setOtpSent(false);
      setFormData(prev => ({ ...prev, otp: '', password: '' }));
    } else {
      setIsOtpMode(false);
      setOtpSent(false);
      setFormData(prev => ({ ...prev, otp: '' }));
    }
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
      return;
    }

    if (name === 'password') {
      if (value.length > 20) {
        toast.error('Password cannot exceed 20 characters!');
        return; // stops user from typing more
      }
      setFormData(prev => ({ ...prev, [name]: value }));
      return;
    }
    if (name === 'email') {
      setFormData(prev => ({ ...prev, email: value }));
      setOtpSent(false); // ✅ hide OTP/resend when email changes
      setFormData(prev => ({ ...prev, otp: '' }));
      return;
    }

    if (name === 'otp') {
      const numericValue = value.replace(/[^0-9]/g, '').slice(0, 6);
      setFormData(prev => ({ ...prev, otp: numericValue }));
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };


  const sessionDuration = formData.rememberMe ? 60 * 60 * 24 : 60 * 60;

  const normalizeRole = (role) => (role ? role.toString().trim().toLowerCase() : '');

  const navigateForRole = (role) => {
    if (role === 'hospital') {
      navigate('/hospital-dashboard');
    } else if (role === 'professional') {
      navigate('/professional-dashboard');
    } else if (role === 'patient') {
      navigate('/patient-dashboard');
    } else {
      navigate('/admin/dashboard');
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate email/mobile
    if (!validateEmailOrMobile(formData.email)) {
      toast.error('Please enter a valid email ID or mobile number (10-15 digits)');
      return;
    }

    if (isOtpMode) {
      // OTP validation
      if (!otpSent) {
        toast.error('Please send OTP first');
        return;
      }

      if (!formData.otp || formData.otp.length !== 6) {
        toast.error('Please enter a valid 6-digit OTP');
        return;
      }

      setIsValidatingOtp(true);
      try {
        const response = await client.get(
          `/api/otp/validate-otp?emailID=${encodeURIComponent(formData.email)}&otp=${encodeURIComponent(formData.otp)}`
        );
        const { success, message, data } = response.data || {};

        if (!success || !data) {
          toast.error(message || 'OTP validation failed');
          setIsValidatingOtp(false);
          return;
        }

        const normalizedRole = normalizeRole(data?.role);
        const serverExpiryMs = data?.expiresAt ? Date.parse(data.expiresAt) : null;
        const safeExpiresAtMs = Number.isFinite(serverExpiryMs)
          ? serverExpiryMs
          : Date.now() + sessionDuration * 1000;
        const expiresInSeconds = Math.max(
          60,
          Math.floor((safeExpiresAtMs - Date.now()) / 1000),
        );

        if (!data?.token || !normalizedRole) {
          toast.error('Missing token or role in response.');
          setIsValidatingOtp(false);
          return;
        }

        saveSession({
          token: data.token,
          role: normalizedRole,
          expiresIn: expiresInSeconds,
          expiresAt: safeExpiresAtMs,
          displayName: data?.displayName,
          professionalId: data?.professionalID ?? data?.professionalId ?? null,
          hospitalId: data?.hospitalID ?? data?.hospitalId ?? null,
          patientId: data?.patientID ?? data?.patientId ?? null,
          patientEmail: normalizedRole === 'patient' ? (data?.emailID || formData.email) : null,
          photoFileBytes: data?.photoFileBytes ?? null,
          photoFileName: data?.photoFileName ?? null,
          userId: data?.userID ?? data?.userId ?? null,
        });

        toast.success(message || 'Login successful.', { autoClose: 1200 });

        setTimeout(() => {
          navigateForRole(normalizedRole);
        }, 1200);
      } catch (err) {
        const apiMessage = err?.response?.data?.message;
        toast.error(apiMessage || 'OTP validation failed. Please try again.');
        setIsValidatingOtp(false);
      }
      return;
    }

    // Password login
    if (!formData.password) {
      toast.error('Please enter your password');
      return;
    }

    setIsLoggingIn(true);
    try {
      const loginPayload = {
        emailID: formData.email,
        password: formData.password,
      };

      const response = await client.post('/api/users/login', loginPayload);
      const { success, message, data } = response.data || {};

      if (!success) {
        toast.error(message || 'Login failed.');
        return;
      }

      const normalizedRole = normalizeRole(data?.role);

      const serverExpiryMs = data?.expiresAt ? Date.parse(data.expiresAt) : null;
      const safeExpiresAtMs = Number.isFinite(serverExpiryMs)
        ? serverExpiryMs
        : Date.now() + sessionDuration * 1000;
      const expiresInSeconds = Math.max(
        60,
        Math.floor((safeExpiresAtMs - Date.now()) / 1000),
      );

      if (!data?.token || !normalizedRole) {
        toast.error('Missing token or role in login response.');
        return;
      }

      saveSession({
        token: data.token,
        role: normalizedRole,
        expiresIn: expiresInSeconds,
        expiresAt: safeExpiresAtMs,
        displayName: data?.displayName,
        professionalId: data?.professionalID ?? data?.professionalId ?? null,
        hospitalId: data?.hospitalID ?? data?.hospitalId ?? null,
        patientId: data?.patientID ?? data?.patientId ?? null,
        patientEmail: normalizedRole === 'patient' ? (data?.emailID || formData.email) : null,
        photoFileBytes: data?.photoFileBytes ?? null,
        photoFileName: data?.photoFileName ?? null,
        userId: data?.userID ?? data?.userId ?? null,
      });

      toast.success(message || 'Login successful.', { autoClose: 1200 });

      setTimeout(() => {
        navigateForRole(normalizedRole);
      }, 1200);
    } catch (err) {
      const apiMessage = err?.response?.data?.message;
      toast.error(apiMessage || 'Unable to login. Please try again.');
    } finally {
      setIsLoggingIn(false);
    }
  };

  return (
    <div className="App">
      <Header />

      <section className="login-section">
        <div className="container">
          <div className="row justify-content-center align-items-center min-vh-50">
            <div className="col-lg-5 col-md-8 col-12">
              <div className="login-card">
                <div className="login-header text-center mb-4">
                  {showLogo && (
                    <img src={logo} alt="24/7 ProCare Logo" className="logo-img mb-3" />
                  )}
                  <h2 className="login-title">Sign In</h2>
                  <p className="login-subtitle mt-3">
                    Welcome back! Login for Admin, Hospital, Doctor, or Patient.
                  </p>
                  {registeredEmail && (
                    <p className="text-success small mt-2 mb-0">
                      Registration complete
                      {registeredName ? ` for ${registeredName}` : ''}. Sign in with{' '}
                      <strong>{registeredEmail}</strong>
                    </p>
                  )}
                </div>

                <form className="login-form" onSubmit={handleSubmit}>
                  {/* Email ID/Mobile Number Input */}
                  <div className="mb-3">
                    <label htmlFor="email" className="form-label">
                      <FaEnvelope className="me-2" />Email ID
                    </label>
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Enter your email ID"
                        maxLength="60"
                        required
                        disabled={isOtpMode && otpSent}
                      />
                      {isOtpMode && !otpSent && (
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleSendOtp}
                          disabled={isSendingOtp}
                        >
                          {isSendingOtp ? 'Sending...' : 'Send OTP'}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Password Input */}
                  {!isOtpMode && (
                    <div className="mb-3" id="passwordSection">
                      <label htmlFor="password" className="form-label">
                        <FaLock className="me-2" />Password
                      </label>
                      <div className="input-group">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          className="form-control"
                          id="password"
                          name="password"
                          value={formData.password}
                          onChange={handleChange}
                          placeholder="Enter your password"
                          required
                        />
                        <button
                          className="btn btn-outline-secondary"
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* OTP Input + Resend */}
                  {isOtpMode && otpSent && (
                    <>
                      <div className="mb-3" id="otpSection">
                        <label htmlFor="otp" className="form-label">
                          <FaKey className="me-2" />Enter OTP
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="otp"
                          name="otp"
                          value={formData.otp}
                          onChange={handleChange}
                          placeholder="Enter 6-digit OTP"
                          maxLength="6"
                          pattern="[0-9]{6}"
                          required
                        />
                        <small className="form-text text-muted">
                          6-digit OTP sent to your registered email
                        </small>
                      </div>

                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <button
                          type="button"
                          className="btn btn-link p-0"
                          onClick={handleResendOtp}
                          disabled={isSendingOtp}
                        >
                          {isSendingOtp ? 'Resending...' : 'Resend OTP'}
                        </button>

                        <button
                          type="button"
                          className="btn btn-link p-0"
                          onClick={() => {
                            setOtpSent(false);                 // hide OTP + buttons
                            setFormData((p) => ({ ...p, otp: '' })); // clear otp
                          }}
                          disabled={isSendingOtp || isValidatingOtp}
                        >
                          Change Email
                        </button>
                      </div>
                    </>
                  )}

                  {/* Remember Me & Forgot Password / Login with Email OTP */}
                  {!isOtpMode && (
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <div className="form-check" id="rememberMeSection">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="rememberMe"
                          name="rememberMe"
                          checked={formData.rememberMe}
                          onChange={handleChange}
                        />
                        <label className="form-check-label" htmlFor="rememberMe">
                          Remember me
                        </label>
                      </div>
                      <div className="d-flex gap-2">
                        <Link
                          to="/forgot-password"
                          className="forgot-password-link"
                          id="forgotPasswordLink"
                        >
                          Forgot Password?
                        </Link>
                      </div>
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    className="btn login-submit-btn w-100 mb-3"
                    disabled={
                      isOtpMode
                        ? (isValidatingOtp || !otpSent)
                        : isLoggingIn
                    }
                  >
                    <FaSignInAlt className="me-2" />
                    {isOtpMode
                      ? (isValidatingOtp ? 'Validating...' : 'Verify OTP')
                      : (isLoggingIn ? 'Logging in...' : 'Login')
                    }
                  </button>

                  {/* Register Link */}
                  {!isOtpMode && (
                    <div className="d-flex justify-content-center align-items-center">
                      <div className="d-flex gap-2">
                        <a href="#" className="otp-login-link" id="otpLoginLink" onClick={toggleOtpMode}>
                          Login with Email OTP
                        </a>
                      </div>
                    </div>

                  )}
                  {isOtpMode && (
                    <div className="d-flex justify-content-center">
                      <a href="#" className="otp-login-link" onClick={toggleOtpMode}>
                        Login with Password
                      </a>
                    </div>
                  )}

                  {!isOtpMode && (
                    <p className="text-center text-muted mt-3 mb-0">
                      New patient? <Link to="/patient-register">Register here</Link>
                    </p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Login;

